import React from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from './ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/Card';
import { Download, Clock, MapPin } from 'lucide-react';
import { toast } from 'react-toastify';
import { getInscricaoQrCode } from '../services/api';

// Função auxiliar para determinar a classe de estilo do status da inscrição
const getInscricaoStatusClass = (status) => {
    switch (status) {
        case 'CONFIRMADA':
            return 'bg-success/10 text-success';
        case 'PENDENTE':
            return 'bg-primary/10 text-primary';
        case 'CANCELADA':
            return 'bg-destructive/10 text-destructive';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

function InscricaoCard({ inscricao, isListMode }) {
    const eventoNome = inscricao.evento?.nome || 'Evento Desconhecido';
    const dataInscricao = new Date(inscricao.dataInscricao).toLocaleDateString();
    const cargaHoraria = inscricao.evento?.cargaHorariaTotal || 'N/A';
    const localEvento = inscricao.evento?.local || 'N/A';

    const handleDownloadQrCode = async () => {
        try {
            const downloadToastId = toast.loading("A preparar QR Code para download...");
            const imageBlob = await getInscricaoQrCode(inscricao.id);
            const imageUrl = URL.createObjectURL(imageBlob);
            const link = document.createElement('a');
            link.href = imageUrl;
            link.download = `qrcode_${eventoNome.replace(/\s/g, '_')}_${inscricao.id}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(imageUrl);
            toast.update(downloadToastId, { render: "QR Code baixado com sucesso!", type: "success", isLoading: false, autoClose: 5000 });
        } catch (error) {
            console.error('Erro ao baixar QR Code:', error);
            toast.error(`Erro ao baixar QR Code: ${error.message}`);
        }
    };

    const baseClasses = "transition-all duration-200 ease-in-out hover:shadow-md";
    const listClasses = "sm:flex-row sm:items-center sm:p-4 sm:space-x-4 hover:shadow-md hover:-translate-y-0";
    const gridClasses = "h-full";

    return (
        <Card className={`${baseClasses} ${isListMode ? listClasses : gridClasses}`}>
            <Link to={`/eventos/${inscricao.evento.id}`} className={`block h-full ${isListMode ? 'sm:flex sm:flex-grow sm:items-center' : 'flex flex-col'}`}>
                <CardHeader className={`pb-0 ${isListMode ? 'flex-grow sm:pb-4' : 'pb-0'}`}>
                    <CardTitle className={`leading-tight ${isListMode ? 'text-lg mb-1' : 'mb-1'}`}>{eventoNome}</CardTitle>
                    <CardDescription className="leading-relaxed text-xs">Inscrito em: {dataInscricao}</CardDescription>
                    <div className={`flex items-center mt-2 ${isListMode ? 'hidden md:flex' : 'flex'}`}>
                        <p className="text-sm font-medium text-muted-foreground mr-2">Status:</p>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getInscricaoStatusClass(inscricao.statusPlainText)}`}>
                            {inscricao.statusPlainText}
                        </span>
                    </div>
                </CardHeader>
                
                <CardContent className={`flex-grow flex ${isListMode ? 'flex-row justify-end items-center sm:p-4 sm:pl-0' : 'flex-col justify-between items-start'} pt-2 sm:pt-6`}>
                    
                    {/* Detalhes do Evento (Visível em ambos, mas formatado diferente) */}
                    <div className={`space-y-1 ${isListMode ? 'flex-grow hidden sm:block' : 'block'}`}>
                        <div className="flex items-center text-sm text-muted-foreground">
                            <MapPin className="mr-2 h-4 w-4" />
                            <span>Local: {localEvento}</span>
                        </div>
                        <div className="flex items-center text-sm text-muted-foreground">
                            <Clock className="mr-2 h-4 w-4" />
                            <span>Carga Horária: {cargaHoraria}h</span>
                        </div>
                    </div>

                    {/* QR Code e Download */}
                    {inscricao.codigoQr ? (
                        <div className={`text-center p-2 bg-white rounded-md ${isListMode ? 'flex-shrink-0' : 'mt-3 w-full'}`}>
                            <h5 className="text-sm font-medium mb-2 text-muted-foreground hidden sm:block">Seu QR Code</h5>
                            <div className={`flex justify-center bg-white p-1 rounded-lg shadow-inner ${isListMode ? '' : 'mx-auto w-fit'}`}>
                                <QRCodeSVG value={inscricao.codigoQr} size={isListMode ? 80 : 120} level="H" />
                            </div>
                            <Button variant="link" className="mt-2 h-8 text-xs" onClick={(e) => { e.preventDefault(); handleDownloadQrCode(); }}>
                                <Download className="mr-1 h-3 w-3" /> Baixar
                            </Button>
                        </div>
                    ) : (
                        <div className={`text-center p-2 bg-white rounded-md ${isListMode ? 'flex-shrink-0' : 'mt-3 w-full'}`}>
                            <p className="text-sm text-muted-foreground">QR Code não disponível.</p>
                        </div>
                    )}
                </CardContent>
            </Link>
        </Card>
    );
}

export default InscricaoCard;